import { fetchFastApi } from "../../api/fastapi";
import {
  useHookCacheQuery,
  type UseHookCacheQueryResult,
} from "../../lib/hookCache";
import {
  sourceRowsPageSchema,
  type SourceRowsPage,
} from "../../models/audit/procedureSessionTableShell";

interface ProcedureSessionSourceRowsOptions {
  page?: number;
  pageSize?: number;
}

/**
 * Hook for the slim paginated list endpoint:
 * `GET /controls/v2/procedure-session/{id}/source-rows`.
 *
 * Returns envelopes with `documentCount` and `data` for card rendering — no
 * `matchItems`, no `requiredDocumentsActions`, no test results. Use
 * `useSourceRow(procedureSessionId, sourceRowId)` for the on-click fetch
 * that supplies those heavy fields.
 */
export function useProcedureSessionSourceRows(
  procedureSessionId: string | undefined,
  { page = 1, pageSize = 100 }: ProcedureSessionSourceRowsOptions = {},
): UseHookCacheQueryResult<SourceRowsPage> {
  return useHookCacheQuery<SourceRowsPage>({
    key: [
      "procedure-session",
      procedureSessionId ?? "",
      "source-rows",
      { page, pageSize },
    ],
    fetcher: async () => {
      const json = await fetchFastApi({
        method: "GET",
        path: `/controls/v2/procedure-session/${procedureSessionId}/source-rows`,
        query: { page: String(page), page_size: String(pageSize) },
      });
      return sourceRowsPageSchema.parse(json);
    },
    enabled: procedureSessionId !== undefined,
  });
}
